const $$splitComponentImporter = () => import('/app/apps/web/src/routes/_app/lists/$id/index.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
const $$splitLoaderImporter = () => import('/app/apps/web/src/routes/_app/lists/$id/index.tsx?tsr-split=loader');
import { lazyFn } from '@tanstack/react-router';
import { createFileRoute } from "@tanstack/react-router";
import { createTranslator } from "use-intl";
import { buildPublicListPreviewData } from "@/components/lists/buildPublicListPreviewData";
import { appOrigin } from "@/lib/absolute-url";
import { buildPagePreviewMetaTags } from "@/lib/buildPagePreviewMetaTags";
import { jsonLdScripts } from "@/lib/jsonLdScripts";
import { isIndexablePublicList } from "@/lib/non-catalog-seo-policy";
import { withSiteTitle } from "@/lib/page-title";
import { pickEnum, pickString } from "@/lib/search-params";
import { PRIVATE_PAGE_ROBOTS, ROLLOUT_NOINDEX_ROBOTS } from "@/lib/seo-meta";
import { getRootLoaderData } from "@/types/intl";
import { FORMAT_CATEGORIES_WITH_OTHER } from "@garimpa/lib/formatGroups";
const VALID_VIEWS = ["grid", "browse"] as const;
const VALID_DIMENSIONS = ["country", "artist", "year", "genre"] as const;
const VALID_FORMATS = ["all", ...FORMAT_CATEGORIES_WITH_OTHER] as const;
const VALID_OFFER_SORTS = ["deals", "best_price"] as const;
export const Route = createFileRoute("/_app/lists/$id/")({
  validateSearch: (raw: Record<string, unknown>): ListSearch => {
    const pickRaw = (key: string) => raw[key] as string | string[] | undefined;
    return {
      view: pickEnum(pickRaw("view"), VALID_VIEWS),
      by: pickEnum(pickRaw("by"), VALID_DIMENSIONS),
      sel: pickString(pickRaw("sel")),
      search: pickString(pickRaw("search")),
      format: pickEnum(pickRaw("format"), VALID_FORMATS) as ListSearch["format"],
      country: pickString(pickRaw("country")),
      year: pickString(pickRaw("year")),
      grade: pickString(pickRaw("grade")),
      artist: pickString(pickRaw("artist")),
      genre: pickString(pickRaw("genre")),
      offerSort: pickEnum(pickRaw("offerSort"), VALID_OFFER_SORTS)
    };
  },
  loaderDeps: () => ({}),
  loader: lazyFn($$splitLoaderImporter, 'loader'),
  head: async ({
    match,
    loaderData
  }) => {
    const {
      messages,
      locale
    } = getRootLoaderData(match);
    const tMeta = createTranslator({
      locale,
      messages,
      namespace: "globals.metadata"
    });
    const tLists = createTranslator({
      locale,
      messages,
      namespace: "globals.lists"
    });
    const list = loaderData?.list;
    if (!list) {
      return {
        meta: [{
          title: withSiteTitle(tMeta("myLists"))
        }]
      };
    }
    const indexable = isIndexablePublicList({
      isPublic: list.isPublic,
      itemCount: loaderData.itemCount
    });
    const title = list.isPublic ? tLists("publicListSeoTitle", {
      name: list.name
    }) : list.name;
    const metaData: SeoMetaData = {
      title,
      socialTitle: list.name
    };
    const metaOptions: SeoMetaOptions = {
      robots: indexable ? null : list.isPublic ? ROLLOUT_NOINDEX_ROBOTS : PRIVATE_PAGE_ROBOTS,
      openGraph: false,
      twitter: false
    };
    let canonicalUrl: string | null = null;
    if (list.isPublic) {
      const url = `${appOrigin()}/lists/${list.id}`;
      if (indexable) canonicalUrl = url;
      Object.assign(metaData, buildPublicListPreviewData(list, loaderData.collageReleaseIds, tLists("publicPreviewDescription")));
      metaData.canonicalUrl = url;
      metaOptions.openGraph = {
        type: "website",
        image: {
          width: "600",
          height: "315"
        }
      };
      metaOptions.twitter = {
        card: "summary_large_image",
        includeTitle: false,
        includeDescription: false
      };
    }
    const meta = await buildPagePreviewMetaTags(metaData, metaOptions, {
      locale
    });
    return {
      meta,
      links: canonicalUrl ? [{
        rel: "canonical",
        href: canonicalUrl
      }] : undefined,
      scripts: jsonLdScripts(indexable ? (await import("@/utils/jsonld")).buildListJsonLd(list) : null)
    };
  },
  component: lazyRouteComponent($$splitComponentImporter, 'component')
});